import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ProfitabilityCalculator } from "../components/ProfitabilityCalculator";
import { MultiSelectDropdown } from "../components/FilterBar";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useUser } from "../context/UserContext";

interface FinancialSummary {
  cashCollected: number;
  closerPayouts: number;
  setterPayouts: number;
  takeHome: number;
  totalExpenses: number;
  netProfit: number;
  callsBooked?: number;
  showRate?: number;
  closeRate?: number;
  avgDealSize?: number;
}

export function RevProjector() {
  const { formatForApi } = useDateRange();
  const location = useLocation();
  const { user, getVisibleOffers } = useUser();
  const { offer, offers, setOffers } = useFilters();
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [availableOffers, setAvailableOffers] = useState<Array<{ value: string; label: string }>>([]);

  const isAgencyRoute = location.pathname.startsWith("/agency/");
  const isMember = user?.role === "member";
  const selectedOffers = useMemo(
    () => offers.filter((selectedOffer) => selectedOffer && selectedOffer !== "all"),
    [offers]
  );

  const visibleOfferOptions = useMemo(() => {
    if (!user) return availableOffers;
    const allowed = new Set(getVisibleOffers(availableOffers.map((entry) => entry.value)));
    return availableOffers.filter((entry) => allowed.has(entry.value));
  }, [availableOffers, getVisibleOffers, user]);

  const fetchAvailableOffers = useCallback(async () => {
    try {
      const response = await fetch("/api/take-home-config/offers");
      const result = await response.json();
      if (result.success && Array.isArray(result.offers)) {
        const normalized = result.offers
          .filter(Boolean)
          .map((entry: unknown) => {
            if (typeof entry === "string") return { value: entry, label: entry };
            if (entry && typeof entry === "object" && "value" in entry) {
              const option = entry as { value: string; label?: string };
              return { value: option.value, label: option.label || option.value };
            }
            return { value: String(entry), label: String(entry) };
          })
          .sort((left: { label: string }, right: { label: string }) => left.label.localeCompare(right.label));
        setAvailableOffers(normalized);
      }
    } catch (error) {
      console.error("Failed to fetch rev projector offers:", error);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      setIsLoading(true);
      const dates = formatForApi();
      const params = new URLSearchParams({
        startDate: dates.startDate,
        endDate: dates.endDate,
      });

      if (selectedOffers.length > 0) {
        params.set("offer", selectedOffers.join(","));
      } else if (!isAgencyRoute && offer && offer !== "all") {
        params.set("offer", offer);
      }

      const res = await fetch(`/api/financials/summary?${params.toString()}`);
      const data = await res.json();

      if (data.success) {
        setSummary({
          cashCollected: data.summary.cashCollected || 0,
          closerPayouts: data.summary.closerPayouts || 0,
          setterPayouts: data.summary.setterPayouts || 0,
          takeHome: data.summary.takeHome || 0,
          totalExpenses: data.summary.totalExpenses || 0,
          netProfit: data.summary.netProfit || 0,
          callsBooked: data.summary.callsBooked || 0,
          showRate: data.summary.showRate || 0,
          closeRate: data.summary.closeRate || 0,
          avgDealSize: data.summary.avgDealSize || 0,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [formatForApi, isAgencyRoute, offer, selectedOffers]);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    void fetchAvailableOffers();
  }, [fetchAvailableOffers]);

  return (
    <div className="mx-auto flex max-w-[1600px] flex-col gap-4 sm:gap-6 px-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1 sm:space-y-2">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Revenue Projection</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Scenario modeling based on the current date range and selected offer scope.
          </p>
        </div>
        {!isMember && visibleOfferOptions.length > 0 && (
          <MultiSelectDropdown
            label="All Offers"
            options={visibleOfferOptions}
            value={selectedOffers}
            onChange={(value) => setOffers(value)}
          />
        )}
      </div>

      {isLoading && !summary ? (
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="h-[720px] animate-pulse rounded-xl bg-muted/40" />
        </div>
      ) : (
        <ProfitabilityCalculator actuals={summary} />
      )}
    </div>
  );
}
